import {
  Group,
  Mesh,
  PlaneGeometry,
} from "three";

import type { BuildingRenderConfig } from "../../config/buildings.ts";
import { pointInPolygon, ringCentroid } from "../../world/geometry.ts";
import type { DistrictVisualSample } from "../../world/schema/districtSample.ts";
import type { RuntimeTreeInstance } from "../../world/schema/runtimeTile.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";
import type { RenderVector3 } from "../types.ts";
import { createStylizedBuildingBatch } from "./stylizedBuildings.ts";
import { createRuntimeTreeBatch } from "./trees.ts";

export interface DistrictSampleView {
  readonly object: Group;
  readonly facingYaw: number;
  readonly buildingCount: number;
  readonly treeCount: number;
  dispose(): void;
}

export function districtSampleFacingYaw(
  sample: Readonly<DistrictVisualSample>,
): number {
  let nearest:
    | { readonly x: number; readonly z: number; readonly distanceM: number }
    | null = null;
  for (const building of sample.buildings) {
    const centroid = ringCentroid(building.footprint.outer);
    const distanceM = Math.hypot(
      centroid.x - sample.viewpoint.presentationLocal.x,
      centroid.z - sample.viewpoint.presentationLocal.z,
    );
    if (nearest === null || distanceM < nearest.distanceM) {
      nearest = { ...centroid, distanceM };
    }
  }
  if (nearest === null) {
    return 0;
  }
  return Math.atan2(
    nearest.x - sample.viewpoint.presentationLocal.x,
    nearest.z - sample.viewpoint.presentationLocal.z,
  );
}

export function createDistrictSampleView(
  sample: Readonly<DistrictVisualSample>,
  materials: CityMaterialLibrary,
  buildingConfig: Readonly<BuildingRenderConfig>,
  viewpointWorldPosition: Readonly<RenderVector3>,
): DistrictSampleView {
  const object = new Group();
  object.name = `district-style-study:${sample.id}`;
  object.position.set(
    viewpointWorldPosition.x - sample.viewpoint.presentationLocal.x,
    viewpointWorldPosition.y,
    viewpointWorldPosition.z - sample.viewpoint.presentationLocal.z,
  );
  object.userData = {
    sampleId: sample.id,
    sourceId: sample.sourceId,
    verticalBasis: sample.verticalBasis,
    presentation: "translated-source-footprint-style-study",
  };

  const groundGeometry = new PlaneGeometry(
    sample.localBounds.maxX - sample.localBounds.minX + 24,
    sample.localBounds.maxZ - sample.localBounds.minZ + 24,
    1,
    1,
  );
  groundGeometry.rotateX(-Math.PI / 2);
  groundGeometry.translate(
    (sample.localBounds.minX + sample.localBounds.maxX) / 2,
    -0.04,
    (sample.localBounds.minZ + sample.localBounds.maxZ) / 2,
  );
  const ground = new Mesh(groundGeometry, materials.landCover);
  ground.name = `${object.name}:seasonal-ground`;
  ground.receiveShadow = true;
  object.add(ground);

  const buildings = createStylizedBuildingBatch(
    sample.buildings,
    materials,
    buildingConfig,
    {
      tileId: sample.id,
      tileOrigin: { x: 0, z: 0 },
      styleFamilyOverride: sample.districtStyle,
    },
  );
  buildings.object.userData = {
    ...buildings.object.userData,
    sourceBoundsLks94: { ...sample.sourceBoundsLks94 },
    heightBasis: "district-fallback-unmeasured",
  };
  object.add(buildings.object);

  const treePositions = inferredTreePositions(sample);
  const treeInstances: readonly RuntimeTreeInstance[] = treePositions.map(
    (position, index) => ({
      id: `${sample.id}:tree:${index}`,
      sourceId: sample.sourceId,
      sourceFeatureId: `${sample.id}:inferred-tree-cover`,
      kind: "tree",
      family: "deciduous_planting",
      position,
      baseElevationM: 0,
      heightM: 11.5 + ((index * 37) % 9) * 0.16,
      crownRadiusM: 3.55,
      yawRadians: ((index * 71) % 360) * Math.PI / 180,
      placement: "source-land-cover-deterministic-sampling",
    }),
  );
  const treeBatch = createRuntimeTreeBatch(
    sample.id,
    treeInstances,
    0,
    materials,
  );
  if (treeBatch !== null) {
    treeBatch.object.userData = {
      ...treeBatch.object.userData,
      sourceLayer: "licensed-district-sample-inferred-vegetation",
    };
    object.add(treeBatch.object);
  }

  return {
    object,
    facingYaw: districtSampleFacingYaw(sample),
    buildingCount: sample.buildings.length,
    treeCount: treePositions.length,
    dispose(): void {
      object.traverse((child) => {
        if (child instanceof Mesh) {
          child.geometry.dispose();
        }
      });
      object.removeFromParent();
      object.clear();
    },
  };
}

function inferredTreePositions(
  sample: Readonly<DistrictVisualSample>,
): readonly { readonly x: number; readonly z: number }[] {
  const positions: { x: number; z: number }[] = [];
  for (let z = 24; z <= 296; z += 34) {
    for (let x = 24; x <= 296; x += 34) {
      const jittered = {
        x: x + ((x * 13 + z * 7) % 11) - 5,
        z: z + ((x * 5 + z * 17) % 13) - 6,
      };
      if (
        Math.hypot(
          jittered.x - sample.viewpoint.presentationLocal.x,
          jittered.z - sample.viewpoint.presentationLocal.z,
        ) < 10
      ) {
        continue;
      }
      if (
        sample.buildings.some((building) =>
          pointInPolygon(jittered, building.footprint)
        )
      ) {
        continue;
      }
      positions.push(jittered);
    }
  }
  return positions;
}
